import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trash, FilePdf, FileXls, BookmarkSimple, Plus, MagnifyingGlass, PencilSimple, Copy,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api, API, fmtINR, fmtPct } from "../lib/kostra";
import { useAuth } from "../lib/auth";

export default function LibraryPage() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { guard } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recipes");
      setRecipes(data || []);
    } catch (e) { toast.error("Failed to load recipes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = guard(async (id) => {
    if (!window.confirm("Delete this recipe?")) return;
    try {
      await api.delete(`/recipes/${id}`);
      setRecipes((arr) => arr.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  }, "delete a recipe");

  const duplicate = guard(async (id) => {
    try {
      const { data } = await api.post(`/recipes/${id}/duplicate`);
      toast.success(`Duplicated as "${data.dish_name}"`);
      navigate(`/?edit=${data.id}`);
    } catch { toast.error("Failed to duplicate"); }
  }, "duplicate a recipe");

  const download = guard(async (id, kind, name) => {
    try {
      const { data } = await api.get(`/recipes/${id}/export/${kind}`, { responseType: "blob" });
      const blob = new Blob([data], {
        type: kind === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `KOSTRA_${(name || "recipe").replace(/\s+/g, "_")}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Export failed"); }
  }, "download exports");

  const filtered = (recipes || []).filter((r) =>
    !q || (r.dish_name || "").toLowerCase().includes(q.toLowerCase())
    || (r.category || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="app-shell">
      <Header />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-medium">Recipe Library</div>
            <h1 className="font-display text-3xl md:text-4xl font-medium text-[#0A3B24] mt-2">Saved Recipes</h1>
            <p className="text-sm text-[#4B5563] mt-1">Every recipe you save is securely stored and ready to re-cost.</p>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-72">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search recipes..."
                className="pl-9"
                data-testid="library-search-input"
              />
            </div>
            <Button
              onClick={() => navigate("/")}
              className="bg-[#0A3B24] hover:bg-[#0E5232] text-white"
              data-testid="library-new-btn"
            >
              <Plus size={16} className="mr-1.5" /> New
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="kostra-card p-10 text-center text-sm text-[#4B5563]">Loading recipes...</div>
        ) : filtered.length === 0 ? (
          <div className="kostra-card p-12 text-center" data-testid="library-empty">
            <div className="h-14 w-14 rounded-full bg-[#FDF9EE] border border-[#D4AF37]/40 mx-auto flex items-center justify-center">
              <BookmarkSimple size={26} weight="duotone" color="#0A3B24" />
            </div>
            <h3 className="font-display text-xl mt-4 text-[#0A3B24]">No recipes yet</h3>
            <p className="text-sm text-[#4B5563] mt-1">Build your first recipe in the calculator and save it here.</p>
            <Link to="/" className="inline-block mt-5">
              <Button className="bg-[#0A3B24] hover:bg-[#0E5232] text-white" data-testid="library-empty-cta">
                <Plus size={16} className="mr-1.5" /> Open Calculator
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="library-grid">
            {filtered.map((r, i) => {
              const photoUrl = r.photo_path ? `${API}/files/${r.photo_path}` : null;
              const s = r.summary || {};
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                  className="kostra-card overflow-hidden flex flex-col"
                  data-testid={`recipe-card-${r.id}`}
                >
                  <div className="h-40 bg-[#F9FAFB] relative">
                    {photoUrl ? (
                      <img src={photoUrl} alt={r.dish_name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-[#4B5563] text-xs">No photo</div>
                    )}
                    <span className="absolute top-3 left-3 gold-pill text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full">
                      {r.category || "—"}
                    </span>
                    <button
                      onClick={() => remove(r.id)}
                      className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-full bg-white/95 border border-[#E5E7EB] text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] hover:border-[#DC2626]/30 transition shadow-sm"
                      data-testid={`recipe-delete-${r.id}`}
                      aria-label="Delete recipe"
                      title="Delete recipe"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <button
                      type="button"
                      onClick={() => navigate(`/?edit=${r.id}`)}
                      className="text-left group/title"
                      data-testid={`recipe-open-title-${r.id}`}
                    >
                      <h3 className="font-display text-lg text-[#0A3B24] font-semibold tracking-tight group-hover/title:underline decoration-[#D4AF37] underline-offset-4">
                        {r.dish_name || "Untitled"}
                      </h3>
                    </button>
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      <div>
                        <div className="kostra-data-label">Cost / Portion</div>
                        <div className="font-mono-num text-sm text-[#111827] mt-1 font-semibold">{fmtINR(s.cost_per_portion || 0)}</div>
                      </div>
                      <div>
                        <div className="kostra-data-label">Suggested</div>
                        <div className="font-mono-num text-sm text-[#0A3B24] mt-1 font-semibold">{fmtINR(s.suggested_price || 0)}</div>
                      </div>
                      <div>
                        <div className="kostra-data-label">Margin</div>
                        <div className="font-mono-num text-sm text-[#16A34A] mt-1 font-semibold">{fmtPct(s.gross_margin_pct || 0)}</div>
                      </div>
                    </div>
                    {(s.net_weight_per_portion_g || 0) > 0 && (
                      <div className="grid grid-cols-2 gap-3 mt-3 p-2.5 rounded-md bg-[#FDF9EE] border border-[#D4AF37]/30"
                           data-testid={`recipe-netwt-${r.id}`}>
                        <div>
                          <div className="kostra-data-label text-[10px]">Net Wt / Port.</div>
                          <div className="font-mono-num text-xs text-[#0A3B24] mt-0.5 font-semibold">
                            {Number(s.net_weight_per_portion_g).toFixed(0)} g
                          </div>
                        </div>
                        <div>
                          <div className="kostra-data-label text-[10px]">Cost / Kg</div>
                          <div className="font-mono-num text-xs text-[#0A3B24] mt-0.5 font-semibold">
                            {fmtINR(s.cost_per_kg || 0)}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-5 pt-4 border-t border-[#E5E7EB] space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => navigate(`/?edit=${r.id}`)}
                          variant="outline"
                          className="h-10 border-[#0A3B24]/40 text-[#0A3B24] hover:bg-[#0A3B24]/5 font-semibold"
                          data-testid={`recipe-open-${r.id}`}
                        >
                          <PencilSimple size={16} weight="bold" className="mr-2" /> Open
                        </Button>
                        <Button
                          onClick={() => duplicate(r.id)}
                          variant="outline"
                          className="h-10 border-[#D4AF37]/60 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                          data-testid={`recipe-duplicate-${r.id}`}
                        >
                          <Copy size={16} weight="bold" className="mr-2" /> Duplicate
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          onClick={() => download(r.id, "pdf", r.dish_name)}
                          className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold h-10 w-full"
                          data-testid={`recipe-pdf-${r.id}`}
                        >
                          <FilePdf size={16} weight="bold" className="mr-2" /> PDF
                        </Button>
                        <Button
                          onClick={() => download(r.id, "excel", r.dish_name)}
                          className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-10 w-full"
                          data-testid={`recipe-excel-${r.id}`}
                        >
                          <FileXls size={16} weight="bold" className="mr-2" /> Excel
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
